export type Money = { amount: number | null; currency: string; pending: boolean };

export type PhotoRef = {
  slot: string;
  assetId: string | null;
  focalX: number;
  focalY: number;
  zoom: number;
};

export type Currency = 'EUR' | 'USD' | 'GBP' | 'CHF' | 'IDR';

export type Booklet = {
  schemaVersion: string;
  templateId: string;
  templateVersion: string;
  locale: string;
  tour: {
    title: string;
    eyebrow: string;
    tagline: string;
    coverHeadline: string[];
    durationDays: number;
    season: string;
    year: number | null;
    currency: Currency;
    difficulty: { value: number; of: number };
  };
  route: {
    cities: string[];
    totalDistanceKm: number | null;
    map: {
      mode: 'generate' | 'upload';
      assetId: string | null;
      style: string;
      labelOverrides: Array<{ city: string; dx: number; dy: number }>;
    };
  };
  schedule: Array<{ day: number; title: string; distanceKm: number | null }>;
  days: Array<{ day: number; title: string; body: string; photos: PhotoRef[] }>;
  itinerary: Array<{ destination: string; date: string | null; distanceKm: number | null }>;
  experience: Array<{ label: string; description: string; icon: string; photo: PhotoRef }>;
  packages: Array<{
    code: string;
    name: string;
    pax: number;
    price: Money;
    priceLabel: string;
    includes: string[];
    hotel: string;
  }>;
  bikes: Array<{ brand: string; model: string; price: Money }>;
  addons: Array<{ name: string; unit: string; price: Money }>;
  included: string[];
  notIncluded: string[];
  notes: string[];
  exclusions: string[];
  deposits: Array<{
    code: string;
    name: string;
    amount: number | null;
    surcharge: number | null;
    currency: string;
    points: string[];
  }>;
  contact: {
    address: string;
    email: string;
    website: string;
    instagram: string;
    tiktok: string;
    phones: Array<{ region: string; number: string }>;
  };
  useTemplatePhotos: boolean;
  /** Hex colour of the text printed over the full-page package photo. */
  photoTextColor: string;
  hiddenPages: string[];
  covers: { cover: PhotoRef; dividerA: PhotoRef; dividerB: PhotoRef; pick: PhotoRef; closing: PhotoRef };
  unmapped: Array<{ source: string; paragraphIndex: number | null; text: string }>;
};

export const STATUSES = [
  'NOT_STARTED', 'IN_PROGRESS', 'PENDING', 'REVIEW', 'REVISION', 'APPROVED', 'PUBLISHED',
] as const;
export type Status = (typeof STATUSES)[number];

export const STATUS_LABEL: Record<Status, string> = {
  NOT_STARTED: 'Just created',
  IN_PROGRESS: 'Being written',
  PENDING: 'Waiting for prices',
  REVIEW: 'In review',
  REVISION: 'Needs changes',
  APPROVED: 'Approved',
  PUBLISHED: 'Published',
};

export type Issue = { level: 'blocker' | 'warning'; field: string; message: string };

export type Project = {
  id: string;
  name: string;
  templateId: string;
  templateVersion: string;
  status: Status;
  content: Booklet;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sources: Array<{ type: 'docx' | 'sheet'; filename: string; importedAt: string }>;
  issues?: Issue[];
};

export type ProjectSummary = {
  id: string;
  name: string;
  status: Status;
  title: string;
  durationDays: number;
  templateId: string;
  updatedAt: string;
  photos: number;
  blockers: number;
  openComments: number;
};

export type Asset = {
  id: string;
  projectId: string;
  filename: string;
  storedAs: string;
  mime: string;
  width: number;
  height: number;
  bytes: number;
  url: string;
  lowRes?: boolean;
};

export type Conflict = {
  path: string;
  label: string;
  current: string;
  incoming: string;
  recommended: 'current' | 'incoming';
};

export type ColumnMapping = {
  destination: number | null;
  day: number | null;
  km: number | null;
  pilot: number | null;
  copilot: number | null;
  single: number | null;
};

export type ImportResult = {
  token: string;
  source: 'docx' | 'sheet';
  filename: string;
  conflicts: Conflict[];
  patch: Partial<Booklet>;
  stats?: { paragraphs: number; mapped: number; unmapped: number; photoMarkers: number };
  confidence?: Record<string, 'high' | 'low'>;
  sheets?: string[];
  sheetName?: string;
  mapping?: ColumnMapping;
  autoMapped?: boolean;
  headerRow?: number;
  columns?: Array<{ index: number; letter: string; header: string }>;
  preview?: string[][];
  pending?: string[];
};

export type RenderJob = {
  id: string;
  projectId: string;
  preset: 'print' | 'web';
  status: 'queued' | 'running' | 'done' | 'failed';
  file: string | null;
  error: string | null;
  ms: number | null;
  url: string | null;
};

export type Version = {
  id: string;
  projectId: string;
  number: number;
  pdfPrint: string | null;
  pdfWeb: string | null;
  label: string;
  publishedBy: string;
  publishedAt: string;
  shareToken?: string | null;
};

export type Change = { path: string; from: unknown; to: unknown };

export type Role = 'admin' | 'editor' | 'reviewer' | 'viewer';

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  editor: 'Editor',
  reviewer: 'Reviewer',
  viewer: 'Viewer',
};

export const ROLE_HELP: Record<Role, string> = {
  admin: 'Everything, plus users and templates',
  editor: 'Imports, writes and designs booklets',
  reviewer: 'Comments, approves and publishes',
  viewer: 'Looks and downloads only',
};

export type User = {
  id: string;
  username: string;
  name: string;
  role: Role;
  disabled: boolean;
  createdAt: string;
};

export type Comment = {
  id: string;
  projectId: string;
  fieldPath: string | null;
  page: number | null;
  body: string;
  resolved: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
};

export type HistoryEntry = {
  id: string;
  projectId: string;
  userId: string;
  username: string;
  action: 'create' | 'edit' | 'import' | 'status' | 'publish' | 'restore' | 'duplicate' | 'rename';
  summary: string;
  changes: Change[];
  at: string;
};

export type TemplateTokens = { green: string; cream: string; lime: string; display: string; body: string };

export type Template = {
  id: string;
  name: string;
  version: string;
  builtIn: boolean;
  tokens: TemplateTokens;
  pageOrder: string[];
  updatedAt: string;
  usedBy: number;
};

export type TemplateOptions = {
  displayFonts: string[];
  bodyFonts: string[];
  sections: Array<{ id: string; label: string }>;
  hideablePages: Array<{ id: string; label: string }>;
  currencies: Currency[];
  photoSlots: Record<SlotKind, SlotSpec>;
};

export type SlotKind = 'cover' | 'map' | 'full' | 'day' | 'experience';

/** The size the template prints a photo at — shown on every slot. */
export type SlotSpec = {
  kind: SlotKind;
  label: string;
  widthMm: number;
  heightMm: number;
  widthPx: number;
  heightPx: number;
  ratio: string;
  note?: string;
};

/** One line in the admin-only activity log. */
export type AuditEntry = {
  id: string;
  at: string;
  userId: string;
  username: string;
  action: string;
  target: string;
  detail?: string;
  projectId?: string;
  method: string;
  path: string;
  ip?: string;
};

export type AuditPage = { entries: AuditEntry[]; users: string[]; actions: string[] };

export type Preflight = { issues: Issue[]; pages: number | null; publishable: boolean };
